/*
 * @Descripttion: 画布页面工具栏
 * @Author: yang chenglin
 */
import React, { useState, useContext, memo, useEffect } from 'react';
import { Balloon, DatePicker, Checkbox, Button } from '@alifd/next';
import moment from 'moment';
import LzMsg from '@/components/common/LzMsg';
import LzDialog from '@/components/common/LzDialog';
import Tips from '@/components/UI/Tips';
import { ActivityContext, SaveTarget, PageType } from '../../index';
import styles from './index.module.scss';

/**
 * 菜单按钮
 * @param param0 配置信息
 */
const MenuButton = ({ icon, text, disable = false, onClick }) => {
    return (
        <div
            className={`${styles['tool-btn']} ${
                disable ? styles['disable'] : ''
            }`}
            onClick={() => !disable && onClick()}
        >
            <span
                className={`iconfont ${icon} ${styles['tool-btn-icon']}`}
            ></span>
            {// 创建副本需要文案说明，后续根据产品规划，可能所有都会加一些东西，那时需要重构组件
            icon === 'icon-chuangjianfuben' ? (
                <Balloon.Tooltip
                    align="b"
                    trigger={
                        <span className={styles['tool-btn-label']}>{text}</span>
                    }
                >
                    创建副本组件支持单节点创建和多节点创建：
                    <br />
                    单节点创建副本：单击选中节点后，点击创建副本按钮
                    <br />
                    多节点创建副本：鼠标左键进行多节点框选，框选后点击创建副本按钮
                </Balloon.Tooltip>
            ) : (
                <span className={styles['tool-btn-label']}>{text}</span>
            )}
        </div>
    );
};

const Toolbar = ({ save = (target, type) => {} }) => {
    const {
        pageInfo,
        isSave,
        inSaveProcess,
        canvasInfo,
        changeCanvasInfo,
        graph,
        resetGraph,
        checkAllNodes,
        copyFocusItems,
        toDelete,
        activityInfo,
        commonSendTime, // 统一设置短信富信发送时间的时间值['短信', '富信', '人群包分析', '人群包分析新']
        selectSmsType, // 勾选了哪种类型的节点 boolean ['短信', '富信']
        setNodeExecuteTime
    } = useContext(ActivityContext);

    const disabledDate = function(date, view) {
      return moment().subtract(1, 'day').valueOf() > date.valueOf() || date.valueOf() > moment().add(30, 'days')
    };

    // 由于可能存在点击取消或直接关闭弹窗的情况，所以在组件里自己维护一套数据，防止直接触发父组件值的改变，而事实上本不想设置
    const returnTime = (time, type) => { // time：初始时间； type：0短信 1富信 0人群包分析 0人群包分析新
        let result = time ? moment(time) : moment();
        if (type === 1 && !commonSendTime[1]) {
            result = result.add(1, 'days');
        };
        return result.format('YYYY-MM-DD HH:mm:ss');
    }
    const [commonTime, setCommonTime] = useState([returnTime(commonSendTime[0], 0), returnTime(commonSendTime[1], 1), returnTime(commonSendTime[2], 0), returnTime(commonSendTime[3], 0), returnTime(commonSendTime[4], 0)]);
    const [selectType, setSelectType] = useState(selectSmsType);

    /**
     * 点击还原的事件处理
     */
    const recover = () => {
        resetGraph();
    };

    /**
     * 检测 测试节点的数据和连接是否合理等逻辑
     */
    const detect = async () => {
        try {
            await checkAllNodes();
        } catch (e) {
            LzMsg.error(e.message);
        }
    };

    /**
     * 测试方法。。。
     */
    const test = () => {
        graph.test();
    };

    /**
     * @description: 设置短信富信节点发送时间
     * @return {*}
     */
    const [showTimeDialog, setShowTimeDialog] = useState<boolean>(false);
    const changeType = (val, index: number): void => {
        let temp = [...selectType];
        temp[index] = val;
        setSelectType([...temp]);
    }
    const changeTime = (val, index: number): void => {
        let temp = [...commonTime];
        temp[index] = val;
        setCommonTime([...temp]);
    }
    // 确认设置
    const setAllNodesTime = () => {
        // 校验时间，是否比当前时间大10分钟，如果没有勾选，直接就是真
        // 短信是大于当前时间10分钟，富信是大于当前时间一天，人群包分析是大于当前时间
        const wd = ['10分钟', '一天', '', '', ''];
        const nodeWd = ['短信', '富信', '人群包分析', '人群包分析（新）分析开始', '人群包分析（新）分析结束'];
        const checkTime = (index: number): boolean => {
            const checkList = [
                moment(commonTime[index]).valueOf() - moment().valueOf() > (10 * 60 * 1000),
                moment(commonTime[index]).valueOf() - moment().valueOf() > (24 * 60 * 60 * 1000 + 10 * 60 * 1000),
                moment(commonTime[index]).valueOf() - moment().valueOf() > 0,
                moment(commonTime[index]).valueOf() - moment().valueOf() > 0,
                true, // 人群包分析新的结束时间需要额外校验
            ]
            return selectType[index] ? checkList[index] : true;
        }
        const mapVal = () => {
            Promise.all(commonTime.map((item, index) => {
                const val = checkTime(index);
                if (!val) throw new Error(`${nodeWd[index]}时间应至少大于当前时间${wd[index]}`);
            })
            )
        }
        try {
            mapVal();
            if ((selectType[3] && (moment(commonTime[4]).valueOf() - moment(commonTime[3]).valueOf() < 0))) {
                throw new Error('人群包分析（新）结束时间应大于开始时间')
            }
            // 触发父组件方法
            const realSelectType = [...selectType]; // 索引4跟随索引3变化，因为都是对应人群分析新节点
            realSelectType.splice(4, 1, selectType[3]);
            setNodeExecuteTime(realSelectType, commonTime);
            setShowTimeDialog(false);
        } catch (e) {
            LzMsg.warning(e.message);
        }
    };

    // 为了点击取消时，不保存本次操作，这里监听弹窗变量，实时同步
    useEffect(() => {
        if (showTimeDialog) {
            setCommonTime([returnTime(commonSendTime[0], 0), returnTime(commonSendTime[1], 1), returnTime(commonSendTime[2], 0), returnTime(commonSendTime[3], 0), returnTime(commonSendTime[4], 0)])
            setSelectType(selectSmsType)
        }
    }, [showTimeDialog])

    return (
        <div className={styles['tool-bar']}>
            {pageInfo.type !== PageType.detail && (
                <>
                    {/* isSave是标识这个活动有没有保存过画布流程，保存过就不能进行二次保存了，不过未来会提供更新功能 */}
                    {/* inSaveProcess标识这个活动是不是正在请求保存的过程中，防止多次发请求 */}
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-iconxx-30"
                        text="保存"
                        onClick={() =>
                            inSaveProcess ? {} : save(SaveTarget.process)
                        }
                    />
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-icon-07"
                        text="删除组件"
                        onClick={toDelete}
                    />
                </>
            )}
            <MenuButton
                // disable={pageInfo.type === PageType.preview} // 20231121需求变更，要求查看预执行时也可进行放大缩小
                icon="icon-iconzidingyi-20"
                text="放大"
                onClick={() => graph.zoom(1)}
            />
            <MenuButton
                // disable={pageInfo.type === PageType.preview}
                icon="icon-iconzidingyi-21"
                text="缩小"
                onClick={() => graph.zoom(-1)}
            />
            <MenuButton
                disable={pageInfo.type === PageType.preview}
                icon="icon-iconzidingyi-22"
                text="返回原点"
                onClick={() => recover()}
            />
            {pageInfo.type !== PageType.detail && (
                <>
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-chuangjianfuben"
                        text="创建副本"
                        onClick={copyFocusItems}
                    />
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-jiance"
                        text="检测"
                        onClick={detect}
                    />
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-zhengli"
                        text="整理"
                        onClick={() => graph.tidy()}
                    />
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-iconzidingyi-23"
                        text="撤销"
                        onClick={() => graph.undo()}
                    />
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-huifu"
                        text="恢复"
                        onClick={() => graph.redo()}
                    />
                    {/* <MenuButton disable={pageInfo.type === PageType.preview} icon="icon-iconzidingyi-26" text="发送测试" onClick={test}/> */}
                </>
            )}
            {canvasInfo.hasGrid ? (
                <MenuButton
                    icon="icon-yincang"
                    text="隐藏网格"
                    onClick={() => changeCanvasInfo({ hasGrid: false })}
                />
            ) : (
                <MenuButton
                    icon="icon-iconzidingyi-28"
                    text="显示网格"
                    onClick={() => changeCanvasInfo({ hasGrid: true })}
                />
            )}

            {pageInfo.type !== PageType.detail &&
                activityInfo?.executionPlan?.type === 1 && (
                    <MenuButton
                        disable={pageInfo.type === PageType.preview}
                        icon="icon-icon7ge3-20"
                        text="时间调整"
                        onClick={() => setShowTimeDialog(!showTimeDialog)}
                    />
                )}

            <div className={styles['offset']}></div>
            {/* {
                pageInfo.type === PageType.editor &&
                <Button className="btn-saveTpl" type="primary" text onClick={() => save(SaveTarget.template)}>另存为模版</Button>
            } */}

            {
                showTimeDialog && <LzDialog
                    visible={showTimeDialog}
                    shouldUpdatePosition
                    title="时间调整"
                    onClose={() => {
                        setShowTimeDialog(false);
                    }}
                    style={{ width: '1060px'}}
                    footer={false}
                    isFullScreen
                >
                    <div className={styles.p30}>
                        <Tips content={
                            <div>说明:可统一配置当前画布中，短信节点的投放/分析时间。</div>
                        } />
                        <div>
                            <div className={`${styles.mt20} ${styles.f16} ${styles['gray_color']}`}>选择节点类型：</div>
                            <div>
                                <div className={`${styles.mt10} ${styles.mb10}`}><Checkbox checked={selectType[0]} onChange={(v) => changeType(v, 0)}>官方人群短信节点</Checkbox></div>
                                <div className={styles['flex-start-center']}>
                                    <Balloon.Tooltip
                                        align="b"
                                        trigger={
                                            <span className="iconfont icon-icon4-33" />
                                        }
                                    >
                                        1.短信发送时间范围为: 早9:01-晚 19:59(日常)
                                        <br />
                                        2.选择指定时间时，短信会在指定时间发送，发送时间需要在画布提交之后至少10分钟
                                    </Balloon.Tooltip>
                                    <div>发送时间：</div>
                                    <DatePicker
                                        showTime
                                        hasClear={false}
                                        value={
                                            commonTime[0]
                                        }
                                        onChange={(executionTime) => changeTime(executionTime, 0)}
                                    />
                                </div>
                            </div>
                            {/* <div>
                                <div className={`${styles.mt10} ${styles.mb10}`}><Checkbox checked={selectType[1]} onChange={(v) => changeType(v, 1)}>富信节点</Checkbox></div>
                                <div className={styles['flex-start-center']}>
                                    <Balloon.Tooltip
                                        align="b"
                                        trigger={
                                            <span className="iconfont icon-icon4-33" />
                                        }
                                    >
                                        1.富媒体短信发送时间范围为: 早9:01-晚 19:59(日常)
                                        <br />
                                        2.因富媒体短信发送需要京东平台人工审核，因此需要至少提前一天进行创建。请知悉!
                                    </Balloon.Tooltip>
                                    <div>发送时间：</div>
                                    <DatePicker
                                        showTime
                                        hasClear={false}
                                        value={
                                            commonTime[1]
                                        }
                                        onChange={(executionTime) => changeTime(executionTime, 1)}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className={`${styles.mt10} ${styles.mb10}`}><Checkbox checked={selectType[2]} onChange={(v) => changeType(v, 2)}>人群包分析</Checkbox></div>
                                <div className={styles['flex-start-center']}>
                                    <Balloon.Tooltip
                                        align="b"
                                        trigger={
                                            <span className="iconfont icon-icon4-33" />
                                        }
                                    >
                                        可选择近30天的日期；建议选择营销触点完成后的时间，以进行更精准的人群分析
                                    </Balloon.Tooltip>
                                    <div>分析时间：</div>
                                    <DatePicker
                                        disabledDate={disabledDate}
                                        showTime
                                        hasClear={false}
                                        value={
                                            commonTime[2]
                                        }
                                        onChange={(executionTime) => changeTime(executionTime, 2)}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className={`${styles.mt10} ${styles.mb10}`}><Checkbox checked={selectType[3]} onChange={(v) => changeType(v, 3)}>人群包分析（新）</Checkbox></div>
                                <div className={styles['flex-start-center']}>
                                    <Balloon.Tooltip
                                        align="b"
                                        trigger={
                                            <span className="iconfont icon-icon4-33" />
                                        }
                                    >
                                        可选择近30天的日期；建议选择营销触点完成后的时间，以进行更精准的人群分析
                                    </Balloon.Tooltip>
                                    <div>分析时间：</div>
                                    <DatePicker
                                        disabledDate={disabledDate}
                                        showTime
                                        hasClear={false}
                                        value={
                                            commonTime[3]
                                        }
                                        onChange={(executionTime) => changeTime(executionTime, 3)}
                                    /> - <DatePicker
                                            disabledDate={disabledDate}
                                            showTime
                                            hasClear={false}
                                            value={
                                                commonTime[4]
                                            }
                                            onChange={(executionTime) => changeTime(executionTime, 4)}
                                        />
                                </div>
                            </div> */}
                        </div>
                    </div>
                    <div className={styles['set-time-dialog-footer']}>
                        <Button
                            type="normal"
                            className={`cancel-btn ${styles.mr10} ${styles.w88}`}
                            onClick={() => {
                                setShowTimeDialog(false)
                            }}
                        >
                            取消
                        </Button>
                        <Button
                            type="primary"
                            className={`submit-btn ${styles.w88}`}
                            onClick={setAllNodesTime}
                            disabled={!selectType.find(item => item)}
                        >
                            确认
                        </Button>
                    </div>
                </LzDialog>
            }
        </div>
    );
};
export default memo(Toolbar);
